'use client';
import { getCardVariantImages, getCardVariantUrl } from './variant-images';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';
import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { AttributeType, useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { IoIosInformationCircleOutline } from 'react-icons/io';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { CarouselProvider } from '@eliadmin/core/src/components/ui/carousel-context';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

function getColorSegments(colors: string[] | null | undefined, color: string | null | undefined) {
  if (Array.isArray(colors) && colors.length > 0) {
    return colors.map(segment => segment.trim()).filter(Boolean);
  }

  if (!color) {
    return [];
  }

  return color
    .split(/[\s,;/|]+/)
    .map(segment => segment.trim())
    .filter(Boolean);
}

function getVariantUrlForValue(product: Product, attributeId: number | undefined, valueId: number) {
  const url = ROUTES.PRODUCT({ slug: product.slug });
  const matchingVariant = product.variants?.find(variant =>
    variant.values?.some(value => value.attribute_id === attributeId && value.id === valueId),
  );

  return matchingVariant?.id ? `${url}?vid=${matchingVariant.id}` : url;
}

function ProductColorSwatches({ product, className }: { product: Product; className?: string }) {
  const colorAttribute = product.attributes?.find(
    attribute =>
      attribute.name === 'Farba' ||
      attribute.type === AttributeType.COLOR ||
      attribute.type === AttributeType.MULTICOLOR,
  );

  if (!colorAttribute?.values?.length) {
    return null;
  }

  const sortedValues = [...colorAttribute.values].sort(
    (a, b) => (a.position ?? 0) - (b.position ?? 0),
  );

  return (
    <div className={cn('flex w-full items-center justify-start gap-2 py-2', className)}>
      {sortedValues.map(value => {
        const colorSegments = getColorSegments(value.colors, value.color);
        const isMulticolor = colorSegments.length > 1;

        return (
          <Link
            key={`product-${product.id}-color-${value.id}`}
            prefetch={false}
            href={getVariantUrlForValue(product, colorAttribute.id, value.id)}
            aria-label={value.name}
            title={value.name}
            className={cn(
              'relative h-5 w-5 overflow-hidden rounded-full border border-border',
              'ea-product-card__color-swatch',
            )}
            style={!isMulticolor ? { backgroundColor: value.color ?? undefined } : undefined}
          >
            {isMulticolor && (
              <span
                className={cn(
                  'absolute inset-0 flex flex-col overflow-hidden rounded-full',
                  'ea-product-card__color-swatch-segments',
                )}
              >
                {colorSegments.map((segment, index) => (
                  <span
                    key={`product-${product.id}-color-${value.id}-segment-${index}`}
                    className={cn('w-full flex-1', 'ea-product-card__color-swatch-segment')}
                    style={{ backgroundColor: segment }}
                  />
                ))}
              </span>
            )}
          </Link>
        );
      })}
    </div>
  );
}

export type ProductCardProps = {
  product: Product;
  className?: { main?: string };
};

export function ProductCardInternal({ product }: ProductCardProps) {
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const filter = useCategoryStore(state => state.filter);
  const selectedValues = useVariantsStore(state => state.selectedValues);
  const isWholesaler = useCustomerStore(state => state.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    discountDecimalPlaces: 0,
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });

  const filterVariants = getProductFilterVariants(product, filter);
  const variantImages = getCardVariantImages(product.variant_images);
  const singleVariantImage = variantImages.length === 1 ? variantImages[0] : undefined;
  const image =
    singleVariantImage?.image ?? getProductImage(templateSettings, product, filterVariants);
  const productUrl = getProductUrl(product, selectedValues);
  const imageUrl = singleVariantImage
    ? getCardVariantUrl(productUrl, singleVariantImage.variant_id)
    : productUrl;

  const sizeOrder = [
    'XXS',
    'XS',
    'S',
    'M',
    'ML',
    'M/L',
    'L',
    'XL',
    'XXL',
    '12',
    '44',
    '47',
    '49',
    '50',
    '52',
    '54',
    '56',
    '58',
    '60',
    '61',
    '62',
    'R1',
    'R2',
    'R3',
    '16“ koleso',
    '20“ koleso',
    '24“ koleso',
    '26“ koleso',
    'Jedna veľkosť pre väčšinu',
  ];

  function formatPrice(price: number): string {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(price)
      .replace('.', ' ');
  }

  function sortSizes(sizes: string[]) {
    return sizes.sort((a, b) => sizeOrder.indexOf(a) - sizeOrder.indexOf(b));
  }

  const isDiscounted = priceDisplay.showPreviousPrice;
  const ASK_FOR_PRICE = priceDisplay.priceOnRequest;

  const hasImages = variantImages.length > 1;

  return (
    <>
      {/* Desktop version */}
      <div
        className={cn(
          'col-span-1 hidden w-full flex-col items-start justify-start transition-all md:flex',
          'ea-custom-product-card__container',
        )}
      >
        <div className="relative aspect-square max-h-[420px] w-full">
          <div className="absolute left-6 top-6 z-[15] flex w-full flex-row items-start justify-start gap-2">
            {product.availability && (
              <div
                className={cn(
                  'bg-[#45CC02] px-2 py-1.5 font-heading text-sm uppercase text-white lg:text-base',
                  product?.availability?.name === 'Skladom' ? 'bg-[#45CC02]' : 'bg-[#FFA500]',
                )}
              >
                {product.availability.name}
              </div>
            )}
            {product.tags
              ?.sort((a, b) => b.name.length - a.name.length)
              .map(badge => (
                <div
                  key={badge.name}
                  className={cn(
                    'px-2 py-1.5 font-heading text-sm uppercase text-white lg:text-base',
                  )}
                  style={{ backgroundColor: badge.color! }}
                >
                  {badge.name}
                </div>
              ))}
            {isDiscounted && Number(priceDisplay.discountLabel) > 0 && (
              <div
                className={cn(
                  'bg-[#b32d2d] px-2 py-1.5 font-heading text-sm uppercase text-white lg:text-base',
                )}
              >
                Zľava -{priceDisplay.discountLabel}%
              </div>
            )}
          </div>
          {hasImages ? (
            <>
              <Carousel
                enableLoop
                showArrows
                customArrowIcons={{ left: <LuChevronLeft />, right: <LuChevronRight /> }}
                className={{
                  container: 'h-full w-full',
                  wrapper: 'h-full w-full',
                  slide: 'relative h-full w-full',
                  main: 'h-full w-full',
                  arrows:
                    'flex h-8 w-8 items-center justify-center rounded-none border-none bg-primary text-white hover:bg-primary/90 hover:text-white',
                  left_arrow: 'left-4',
                  right_arrow: 'right-4',
                }}
                slides={variantImages.map(variant => (
                  <Link
                    key={variant.variant_code}
                    href={getCardVariantUrl(productUrl, variant.variant_id)}
                    prefetch={false}
                    prefetchOnHover
                    className="relative block h-full w-full"
                  >
                    <Image
                      src={variant?.image?.url}
                      alt={resolveImageAlt(variant?.image, product.name)}
                      fill
                      sizes="auto, 100vw"
                      className="object-contain object-bottom p-6"
                    />
                  </Link>
                ))}
              />
            </>
          ) : (
            <Link
              href={imageUrl}
              prefetch={false}
              prefetchOnHover
              className="relative block h-full w-full"
            >
              <Image
                src={image.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(image, product?.name)}
                fill
                sizes="auto, 100vw"
                className="max-h-[420px] object-contain object-bottom p-6"
              />
            </Link>
          )}
        </div>
        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          className="flex w-full items-center justify-between gap-4 px-6 pb-2 pt-4"
        >
          <h3
            className={cn(
              'ea-product-card__name',
              'font-heading uppercase',
              'line-clamp-1 text-lg font-medium text-foreground lg:text-xl',
            )}
          >
            {product.name}
          </h3>
          <div className="flex flex-col items-end justify-center gap-1">
            {ASK_FOR_PRICE ? (
              <p className="whitespace-nowrap font-heading text-lg font-medium text-primary lg:text-xl">
                {t('text2278')}
              </p>
            ) : (
              <>
                {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                  <span className="whitespace-nowrap text-xs text-[#808080]">
                    {t('text1926')} {formatPrice(priceDisplay.mocPrice)}
                  </span>
                )}
                <div className="flex items-baseline justify-end gap-3">
                  {priceDisplay.showPreviousPrice && (
                    <span className="whitespace-nowrap font-heading text-sm font-normal text-[#808080] line-through">
                      {formatPrice(priceDisplay.beforeDiscount ?? 0)}
                    </span>
                  )}
                  <p className="whitespace-nowrap font-heading text-lg font-medium text-primary lg:text-xl">
                    OD {formatPrice(priceDisplay.current ?? 0)}
                  </p>
                </div>
                {priceDisplay.showMocVocPrices && priceDisplay.secondary != null && (
                  <span className="whitespace-nowrap text-xs text-[#808080]">
                    {t('text1925')} {formatPrice(priceDisplay.secondary)}{' '}
                    {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                  </span>
                )}
              </>
            )}
          </div>
        </Link>

        <ProductColorSwatches product={product} className="px-6" />

        <div className="flex w-full items-center justify-between px-6 py-2 pb-6">
          {Array.isArray(product?.attributes) &&
          product.attributes.find(a => a.name === 'Veľkosť rámu') ? (
            <Link
              prefetchOnHover
              prefetch={false}
              href={productUrl}
              className="flex flex-col items-start justify-start"
            >
              <span className="text-xs font-light text-foreground sm:text-sm">Veľkosť rámu</span>
              <p className="font-heading text-base font-medium uppercase sm:text-lg lg:text-xl">
                {sortSizes(
                  product?.attributes
                    ?.find(a => a.name === 'Veľkosť rámu')
                    ?.values?.map(v => v.name) || [],
                ).join(', ')}
              </p>
            </Link>
          ) : (
            <>
              {Array.isArray(product?.attributes) &&
              product.attributes.find(a => a.name === 'Veľkosť kolies') ? (
                <Link
                  prefetchOnHover
                  prefetch={false}
                  href={productUrl}
                  className="flex flex-col items-start justify-start"
                >
                  <span className="text-xs font-light text-foreground sm:text-sm">
                    Veľkosť kolies
                  </span>
                  <p className="font-heading text-base font-medium uppercase sm:text-lg lg:text-xl">
                    {product?.attributes
                      ?.find(a => a.name === 'Veľkosť kolies')
                      ?.values?.map(v => v.name)
                      .join(', ')}
                  </p>
                </Link>
              ) : (
                <div className="w-1" />
              )}
            </>
          )}
          <div
            className={cn(
              'flex items-center justify-end gap-2',
              'ea-product-card__footer-right-container',
            )}
          >
            {product.availability.name === 'Skladom' && !ASK_FOR_PRICE ? (
              <AddToCartButton
                product={product}
                variant="default"
                showIcon
                sellable={isSellable(product, 1)}
              />
            ) : (
              <Button asChild className="w-full whitespace-nowrap">
                <Link prefetch={false} href={'/kontakt'} aria-label={`Zistiť dostupnosť: ${product.name}`}>
                  ZISTIŤ DOSTUPNOSŤ
                </Link>
              </Button>
            )}
            <AddToFavoritesButton slug={product.slug} isFavorite={false} />
          </div>
        </div>
      </div>

      {/* Mobile version */}
      <div className="relative col-span-1 w-full flex-col items-start justify-start md:hidden">
        <div className="absolute left-4 top-8 z-[15] flex w-full flex-row items-start justify-start gap-2">
          <div
            className={cn(
              'bg-[#45CC02] px-2 py-1.5 font-heading text-sm uppercase text-white lg:text-base',
              product?.availability?.name === 'Skladom' ? 'bg-[#45CC02]' : 'bg-[#FFA500]',
            )}
          >
            {product.availability.name}
          </div>
        </div>
        <div className="relative aspect-square w-full">
          {hasImages ? (
            <>
              <Carousel
                enableLoop
                showArrows
                customArrowIcons={{ left: <LuChevronLeft />, right: <LuChevronRight /> }}
                className={{
                  container: 'h-full w-full',
                  wrapper: 'h-full w-full',
                  slide: 'relative h-full w-full',
                  main: 'h-full w-full',
                  arrows:
                    'flex h-7 w-7 items-center justify-center rounded-none border-none bg-primary text-white hover:bg-primary/90 hover:text-white',
                  left_arrow: 'left-4',
                  right_arrow: 'right-4',
                }}
                slides={variantImages.map(variant => (
                  <Link
                    key={variant.variant_code}
                    href={getCardVariantUrl(productUrl, variant.variant_id)}
                    prefetch={false}
                    prefetchOnHover
                    className="relative block h-full w-full"
                  >
                    <Image
                      src={variant?.image?.url}
                      alt={resolveImageAlt(variant?.image, product.name)}
                      fill
                      sizes="auto, 100vw"
                      className="object-contain object-bottom p-6"
                    />
                  </Link>
                ))}
              />
            </>
          ) : (
            <Link
              href={imageUrl}
              prefetch={false}
              prefetchOnHover
              className="relative block h-full w-full"
            >
              <Image
                src={image.url ?? '/images/placeholder.png'}
                alt={resolveImageAlt(image, product?.name)}
                fill
                sizes="auto, 100vw"
                className="object-contain object-bottom"
              />
            </Link>
          )}
        </div>

        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          className="flex w-full px-4 pb-2 pt-6"
        >
          <h3
            className={cn(
              'font-heading uppercase',
              'line-clamp-1 text-lg font-medium text-foreground lg:text-xl',
            )}
          >
            {product.name}
          </h3>
        </Link>
        <Link
          prefetchOnHover
          prefetch={false}
          href={productUrl}
          className="flex flex-col items-end justify-center gap-1 px-4 py-2"
        >
          {ASK_FOR_PRICE ? (
            <p className="font-heading text-lg font-medium text-primary">{t('text2278')}</p>
          ) : (
            <>
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null && (
                <span className="whitespace-nowrap text-xs text-[#808080]">
                  {t('text1926')} {formatPrice(priceDisplay.mocPrice)}
                </span>
              )}
              <div className="flex items-baseline justify-end gap-3">
                {priceDisplay.showPreviousPrice && (
                  <span className="whitespace-nowrap font-heading text-sm font-normal text-[#808080] line-through">
                    {formatPrice(priceDisplay.beforeDiscount ?? 0)}
                  </span>
                )}
                <p className="font-heading text-lg font-medium text-primary">
                  OD {formatPrice(priceDisplay.current ?? 0)}
                </p>
              </div>
              {priceDisplay.showMocVocPrices && priceDisplay.secondary != null && (
                <span className="whitespace-nowrap text-xs text-[#808080]">
                  {t('text1925')} {formatPrice(priceDisplay.secondary)}{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </span>
              )}
            </>
          )}
        </Link>
        <ProductColorSwatches product={product} className="px-4" />
        <div className="flex w-full items-center justify-between gap-2 px-4 py-2">
          {Array.isArray(product?.attributes) &&
          product.attributes.find(a => a.name === 'Veľkosť rámu') ? (
            <div className="flex flex-col items-start justify-start gap-1.5 md:gap-0">
              <span className="text-sm font-light text-foreground">Veľkosť rámu</span>
              <p className="font-heading text-base font-medium uppercase sm:text-lg lg:text-xl">
                {sortSizes(
                  product?.attributes
                    ?.find(a => a.name === 'Veľkosť rámu')
                    ?.values?.map(v => v.name) || [],
                ).join(', ')}
              </p>
            </div>
          ) : (
            <>
              {Array.isArray(product?.attributes) &&
              product.attributes.find(a => a.name === 'Veľkosť kolies') ? (
                <div className="flex flex-col items-start justify-start">
                  <span className="text-xs font-light text-foreground sm:text-sm">
                    Veľkosť kolies
                  </span>
                  <p className="font-heading text-base font-medium uppercase sm:text-lg lg:text-xl">
                    {product?.attributes
                      ?.find(a => a.name === 'Veľkosť kolies')
                      ?.values?.map(v => v.name)
                      .join(', ')}
                  </p>
                </div>
              ) : (
                <div className="w-1" />
              )}
            </>
          )}
          <div
            className={cn(
              'flex items-center justify-end gap-2',
              'ea-product-card__footer-right-container',
            )}
          >
            {product.availability.name === 'Skladom' && !ASK_FOR_PRICE ? (
              <AddToCartButton
                product={product}
                variant="default"
                showIcon
                sellable={isSellable(product, 1)}
              />
            ) : (
              <Button asChild className="min-w-12 max-w-12 px-0">
                <Link prefetch={false} href={'/kontakt'} aria-label={`Zistiť dostupnosť: ${product.name}`}>
                  <IoIosInformationCircleOutline size={24} />
                </Link>
              </Button>
            )}
            <AddToFavoritesButton slug={product.slug} isFavorite={false} />
          </div>
        </div>
      </div>
    </>
  );
}

export function ProductCard(props: ProductCardProps) {
  return (
    <CarouselProvider>
      <VariantsProvider state={{ product: props.product }}>
        <DialogProvider>
          <ProductCardInternal {...props} />
        </DialogProvider>
      </VariantsProvider>
    </CarouselProvider>
  );
}
